import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import type { ApiError } from '../client/apiError';
import { shouldRetryQuery } from './shouldRetryQuery';

// A chave com namespace é traduzida só na hora do toast; `errorMessage` e `successMessage` ficam para o legado, que já traduz.
export type ApiQueryMeta = {
  errorMessageKey?: string;
  errorMessage?: string;
  successMessage?: string;
};

export type ApiMutationMeta = ApiQueryMeta;

declare module '@tanstack/react-query' {
  interface Register {
    defaultError: ApiError;
    queryMeta: ApiQueryMeta;
    mutationMeta: ApiMutationMeta;
  }
}

export type QueryClientNotify = {
  error: (message: string) => void;
  success: (message: string) => void;
};

type CreateQueryClientOptions = {
  notify: QueryClientNotify;
  // O pacote não conhece o i18n do app; quem cria o client passa a tradução, que pode esperar o namespace carregar.
  translate: (key: string) => string | Promise<string>;
};

const FIVE_MINUTES = 1000 * 60 * 5;

export const createQueryClient = ({ notify, translate }: CreateQueryClientOptions) => {
  const notifyError = (meta: ApiQueryMeta | undefined) => {
    if (meta?.errorMessageKey) {
      const key = meta.errorMessageKey;
      // Sem tradução, o toast ainda sai com a chave, que pelo menos diz o que falhou.
      void Promise.resolve()
        .then(() => translate(key))
        .catch(() => key)
        .then(notify.error);
    } else if (meta?.errorMessage) notify.error(meta.errorMessage);
  };
  const notifySuccess = (meta: ApiQueryMeta | undefined) => {
    if (meta?.successMessage) notify.success(meta.successMessage);
  };

  return new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnMount: false, // Never change data on mount to true - Loop Fetch on map.
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        retry: shouldRetryQuery,
        retryDelay: 1000,
        staleTime: FIVE_MINUTES,
      },
    },
    queryCache: new QueryCache({
      onSuccess: (_data, query) => notifySuccess(query.meta),
      onError: (_error, query) => notifyError(query.meta),
    }),
    mutationCache: new MutationCache({
      onSuccess: (_data, _variables, _context, mutation) => notifySuccess(mutation.meta),
      onError: (_error, _variables, _context, mutation) => notifyError(mutation.meta),
    }),
  });
};
